import type { Rule } from "../types";
import { makeFiring, toIsoDate } from "../util";

const EVIDENCE = "kreider_2017_creatine";

/**
 * Any active daily supplement with no intake on ≥ 5 of the last 7 days.
 * General-purpose adherence nudge; uses creatine evidence as the anchor for
 * the "daily dose habit matters more than perfect timing" rationale.
 */
export const supplementStreakBreak: Rule = {
  id: "supplement_streak_break",
  domain: "health.nutrition",
  topics: ["adherence", "supplementation"],
  evidence_refs: [EVIDENCE],
  title: () => "Supplement intake pattern this week",
  evaluate(ctx) {
    const dailySupps = ctx.supplements.filter(
      (s) => s.status === "active" && s.schedule_frequency === "daily",
    );
    if (dailySupps.length === 0) return null;

    const today = ctx.now;
    const sevenDays: string[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(today.getTime() - i * 86_400_000);
      sevenDays.push(toIsoDate(d));
    }

    const lowFrequency: Array<{ name: string; loggedDays: number }> = [];
    for (const supp of dailySupps) {
      const days = new Set<string>();
      for (const intake of ctx.supplement_intakes) {
        if (intake.supplement_id !== supp.id) continue;
        const day = intake.taken_at.slice(0, 10);
        if (sevenDays.includes(day)) days.add(day);
      }
      if (days.size <= 2) lowFrequency.push({ name: supp.name, loggedDays: days.size });
    }
    if (lowFrequency.length === 0) return null;

    const list = lowFrequency
      .slice(0, 3)
      .map((s) => `${s.name} (${s.loggedDays}/7d)`)
      .join(", ");
    return makeFiring({
      rule_id: "supplement_streak_break",
      now: ctx.now,
      tz: ctx.tz,
      severity: "neutral",
      message: `Logged on 2 or fewer days in the last week: ${list}.`,
      evidence_refs: [EVIDENCE],
      factors: [
        {
          name: "low_frequency_count",
          value: lowFrequency.length,
          contribution: "observation",
          evidence_ref: EVIDENCE,
        },
      ],
      target_surface: "today",
    });
  },
};
